// Shared UI primitives used by App.js and the feature modules (src/finance, …).
import { useState, useEffect } from "react";

// ─── Global Toast System ─────────────────────────────────────────────────────────
let _toastMsg = null;
let _toastColor = "#16a34a";
let _toastCb = null;
export function showToast(msg, color) {
    _toastMsg = msg;
    _toastColor = color || "#16a34a";
    if (_toastCb) _toastCb();
    clearTimeout(window.__foxToastTimer);
    window.__foxToastTimer = setTimeout(() => { _toastMsg = null; if (_toastCb) _toastCb(); }, 3500);
}
export function GlobalToast() {
    const [, forceUpdate] = useState(0);
    useEffect(() => {
        _toastCb = () => forceUpdate(x => x + 1);
        return () => { _toastCb = null; };
    }, []);
    // While visible, re-check a few times a second so the message moves to the top
    // as soon as a form/panel opens (even one opened after the message appeared).
    const [dialogOpen, setDialogOpen] = useState(false);
    useEffect(() => {
        if (!_toastMsg) return undefined;
        const check = () => setDialogOpen(!!document.querySelector('[role="dialog"]'));
        check();
        const t = setInterval(check, 150);
        return () => clearInterval(t);
    });
    if (!_toastMsg) return null;
    return (
        // While a form/panel is open the message shows at the top, so it never covers its fields or buttons.
        <div style={{ position: "fixed", ...(dialogOpen ? { top: "calc(16px + env(safe-area-inset-top))" } : { bottom: 28 }), left: "50%", transform: "translateX(-50%)", background: _toastColor, color: "#fff", padding: "12px 28px", borderRadius: 14, fontWeight: 700, fontSize: 14, zIndex: 99999, boxShadow: "0 4px 24px #0005", whiteSpace: "nowrap", maxWidth: "90vw", overflow: "hidden", textOverflow: "ellipsis", pointerEvents: "none", animation: "toastIn 0.25s ease" }}>
            {_toastMsg}
        </div>
    );
}

// ─── Colors & Styles ─────────────────────────────────────────────────────────────
export const C = {
    bg: "#0a0e1a", card: "#111827", card2: "#1a2235", border: "#1f2d45",
    accent: "#3b82f6", accent2: "#6366f1", green: "#10b981", red: "#ef4444",
    yellow: "#f59e0b", purple: "#8b5cf6", text: "#e2e8f0", muted: "#64748b", white: "#ffffff",
};
export const styles = {
    app: { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" },
    card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 },
    card2: { background: C.card2, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 },
    btn: (color = C.accent) => ({ background: color, color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "opacity .2s" }),
    btnOut: { background: "transparent", color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13 },
    input: { background: "#0d1526", border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 14px", color: C.text, fontSize: 14, width: "100%", outline: "none", boxSizing: "border-box" },
    badge: (color) => ({ background: color + "22", color, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-block" }),
    sidebar: { width: 215, background: C.card, borderRight: `1px solid ${C.border}`, minHeight: "100vh", display: "flex", flexDirection: "column" },
    sideItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "11px 24px", cursor: "pointer", fontWeight: active ? 700 : 500, fontSize: 14, color: active ? C.white : C.muted, background: active ? C.accent + "22" : "transparent", borderLeft: active ? `3px solid ${C.accent}` : "3px solid transparent", transition: "all .15s" }),
    main: { flex: 1, padding: 32, overflowY: "auto" },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 },
    grid4: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${C.border}` },
    td: { padding: "13px 16px", fontSize: 14, borderBottom: `1px solid ${C.border}` },
};


// ─── Mobile Hook ─────────────────────────────────────────────────────────────────
export function useMobile() {
    const [mobile, setMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setMobile(window.innerWidth < 768);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);
    return mobile;
}



// Load Foxbeep logo as base64 for PDF embedding (browser only)
export async function loadLogoBase64() {
    try {
        const res = await fetch("/foxbeep-logo-white.png");
        const blob = await res.blob();
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch { return null; }
}
